import RevealContact from './RevealContact';
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import EmployerSidebar from './EmployerSidebar'; // adjust path to match where it actually lives
import {
  FaPhoneAlt,
  FaWhatsapp,
  FaTimes,
  FaFileAlt,
  FaMapMarkerAlt,
  FaBriefcase,
  FaRupeeSign,
  FaGraduationCap,
  FaCertificate,
  FaLanguage,
  FaQuoteLeft,
  FaRegCopy,
  FaCheck,
  FaDownload,
  FaUsers,
  FaHourglassHalf,
  FaStar,
  FaCommentDots,
  FaBan,
  FaSlidersH,
  FaCalendarAlt,
  FaVenusMars,
  FaRedoAlt,
  FaCheckCircle, 
} from 'react-icons/fa';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from '../pages/Navbar';

const toWhatsAppDigits = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`;
  return digits;
};

const resolvePhotoSrc = (path) => {
  if (!path) return null;
  return path.startsWith('http') ? path : `${BACKEND_URL}/storage/${path}`;
};

const extractLabel = (item) => {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') {
    const known =
      item.name ??
      item.skill_name ??
      item.language_name ??
      item.title ??
      item.label ??
      item.value ??
      null;
    if (known) return known;
    const skipKeys = new Set(['id', 'created_at', 'updated_at', 'pivot']);
    const fallback = Object.entries(item).find(
      ([k, v]) => !skipKeys.has(k) && typeof v === 'string' && v.trim() !== ''
    );
    return fallback ? fallback[1] : null;
  }
  return null;
};

const normalizeNamedList = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((item, i) => {
      const label = extractLabel(item);
      const key = typeof item === 'string' ? `${item}-${i}` : (item?.id ?? i);
      return label ? { key, label } : null;
    })
    .filter(Boolean);
};

const summarizeExperience = (user) => {
  if (user?.total_experience != null) return `${user.total_experience} yrs experience`;
  const list = user?.experiences || [];
  if (list.length === 0) return 'Fresher';
  const years = list.reduce((sum, e) => sum + (Number(e.years) || 0), 0);
  if (years > 0) return `${years} yrs experience`;
  return list.length === 1 ? '1 role on record' : `${list.length} roles on record`;
};

const isFresher = (user) => {
  if (user?.total_experience != null) return Number(user.total_experience) === 0;
  return !(user?.experiences?.length > 0);
};

// Numeric years for range filtering — separate from summarizeExperience()'s
// display string. Falls back to 0 for fresher/unknown so range filters don't
// silently exclude candidates with no experience data at all.
const totalExperienceYears = (user) => {
  if (user?.total_experience != null) return Number(user.total_experience) || 0;
  const list = user?.experiences || [];
  return list.reduce((sum, e) => sum + (Number(e.years) || 0), 0);
};

// expected_salary is stored annually (₹/year, per Profile.jsx) — apna's
// candidate rows show a monthly figure, so this is purely a display
// conversion, not a different stored value.
const formatMonthlySalary = (annual) => {
  if (annual === null || annual === undefined || annual === '') return null;
  const num = Number(annual);
  if (Number.isNaN(num) || num <= 0) return null;
  return `₹${Math.round(num / 12).toLocaleString('en-IN')} / mo`;
};

// Sorts experiences by start_date desc and returns { current, previous } —
// current is the currently_working entry if any, else the most recent.
const getCurrentAndPrevious = (experiences) => {
  if (!Array.isArray(experiences) || experiences.length === 0) return { current: null, previous: null };
  const sorted = [...experiences].sort((a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0));
  const current = sorted.find((e) => e.currently_working) || sorted[0];
  const previous = sorted.find((e) => e !== current) || null;
  return { current, previous };
};

const calcAge = (dob) => {
  if (!dob) return null;
  const birth = new Date(dob);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
};

const genderLabel = (g) => {
  if (!g) return null;
  if (g === 'male') return 'M';
  if (g === 'female') return 'F';
  return g.charAt(0).toUpperCase() + g.slice(1);
};

const daysAgo = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diff <= 0) return 'today';
  if (diff === 1) return '1 day ago';
  return `${diff} days ago`;
};

// Single source of truth for the 9-stage pipeline. Used for both the
// candidate-row quick-status dropdown and the applicant-modal dropdown.
const statusOptions = [
  'applied',
  'screening',
  'shortlisted',
  'interview_round_1',
  'interview_round_2',
  'hr_round',
  'offer',
  'joined',
  'rejected',
];

const statusLabels = {
  applied: 'Applied',
  screening: 'Screening',
  shortlisted: 'Shortlisted',
  interview_round_1: 'Interview (Round 1)',
  interview_round_2: 'Interview (Round 2)',
  hr_round: 'HR Round',
  offer: 'Offer',
  joined: 'Joined',
  rejected: 'Rejected',
   withdrawn: 'Withdrawn',
};

// Palette: violet → amber → emerald → rose
const statusStyles = {
  applied:            { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  screening:          { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  shortlisted:        { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  interview_round_1:  { bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-500' },
  interview_round_2:  { bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-500' },
  hr_round:           { bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-500' },
  offer:              { bg: 'bg-violet-50',  text: 'text-violet-700',  dot: 'bg-violet-500' },
  joined:             { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-600' },
  rejected:           { bg: 'bg-rose-50',    text: 'text-rose-700',    dot: 'bg-rose-500' },
   withdrawn:          { bg: 'bg-gray-100',   text: 'text-gray-600',    dot: 'bg-gray-400' },
};

const STATUS_GROUPS = {
  interview: ['interview_round_1', 'interview_round_2', 'hr_round'],
  selected: ['offer', 'joined'],
};

function ContactActions({ phone, className = '' }) {
  if (!phone) return null;
  const waDigits = toWhatsAppDigits(phone);
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} onClick={(e) => e.stopPropagation()}>
      <a
        href={`tel:${phone}`}
        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-900 text-white hover:bg-slate-700 transition"
      >
        <FaPhoneAlt size={11} /> Call
      </a>
      {waDigits && (
        <a
          href={`https://wa.me/${waDigits}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#25D366] text-white hover:opacity-90 transition"
        >
          <FaWhatsapp size={13} /> WhatsApp
        </a>
      )}
    </div>
  );
}

function PhoneChip({ phone }) {
  const [copied, setCopied] = useState(false);
  if (!phone) return null;

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(phone).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <span className="inline-flex items-center gap-2 text-sm font-semibold text-violet-700 border border-violet-200 bg-violet-50 rounded-lg px-3 py-1.5">
      {phone}
      <button
        type="button"
        onClick={handleCopy}
        aria-label="Copy phone number"
        className="text-violet-500 hover:text-violet-700"
      >
        {copied ? <FaCheck size={12} /> : <FaRegCopy size={12} />}
      </button>
    </span>
  );
}

function MatchRing({ percent, size = 46 }) {
  if (percent == null) return null;
  const pct = Math.max(0, Math.min(100, Math.round(percent)));
  const color = pct >= 80 ? '#10B981' : pct >= 50 ? '#6366F1' : '#F59E0B';
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#F1F5F9" strokeWidth="4" fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth="4"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[10px] font-bold" style={{ color }}>{pct}%</span>
      </div>
    </div>
  );
}

function MatchBreakdown({ breakdown }) {
  if (!breakdown || breakdown.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      <span className="text-xs font-bold text-violet-600 mr-0.5">✦ Matching:</span>
      {breakdown.map((b) => (
        <span
          key={b.key}
          className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${
            b.matched
              ? 'border-violet-300 text-violet-700 bg-violet-50'
              : 'border-gray-200 text-gray-400 bg-gray-50'
          }`}
        >
          {b.matched ? '✓' : '✕'} {b.label}
        </span>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const s = statusStyles[status] || { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-400' };
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold px-2.5 sm:px-3 py-1 rounded-full capitalize whitespace-nowrap ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {statusLabels[status] || status}
    </span>
  );
}

function SectionCard({ icon, title, children }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4">
      <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
        {icon}
        {title}
      </h4>
      {children}
    </div>
  );
}

function FilterSection({ title, icon, open, onToggle, children }) {
  return (
    <div className="border-t border-slate-100 pt-3.5">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between group"
      >
        <span className="flex items-center gap-2.5 text-sm font-semibold text-slate-700 group-hover:text-violet-700 transition-colors">
          <span className="w-7 h-7 rounded-lg bg-violet-50 text-violet-500 flex items-center justify-center shrink-0">
            {icon}
          </span>
          {title}
        </span>
        <span className={`text-slate-400 group-hover:text-violet-500 transition-transform ${open ? 'rotate-180' : ''}`}>
          ▾
        </span>
      </button>
      {open && <div className="mt-2.5 pl-9 ml-1">{children}</div>}
    </div>
  );
}

function Avatar({ name, photoSrc, sizeClass }) {
  return photoSrc ? (
    <img src={photoSrc} alt={name} className={`${sizeClass} rounded-full object-cover shrink-0 ring-2 ring-white shadow-md`} />
  ) : (
    <div className={`${sizeClass} rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shrink-0 ring-2 ring-white shadow-md font-bold`}>
      {name ? name.charAt(0).toUpperCase() : (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="w-[55%] h-[55%]">
          <circle cx="12" cy="8" r="3.4" />
          <path d="M5 20c0-3.6 3.1-6.2 7-6.2s7 2.6 7 6.2" />
        </svg>
      )}
    </div>
  );
}

function StatPill({ icon, iconBg, iconColor, count, label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`min-w-0 flex flex-col items-start sm:flex-row sm:items-center gap-1.5 sm:gap-2 text-left rounded-2xl border px-3 py-2.5 transition ${
        active
          ? 'border-violet-400 bg-violet-100 shadow-sm'
          : 'border-slate-200 bg-slate-50 hover:border-violet-200'
      }`}
    >
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${iconBg} ${iconColor}`}>
        {icon}
      </div>
      <div className="min-w-0 w-full">
        <div className="text-base font-bold text-slate-900 leading-tight">{count}</div>
        <div className="text-[11px] text-slate-500 truncate">{label}</div>
      </div>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Applicant detail modal                                             */
/* ------------------------------------------------------------------ */

function ApplicantModal({ applicant, onClose, onStatusChange }) {
  const [visible, setVisible] = useState(false);
  const { user } = applicant;

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    const onKeyDown = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestClose = () => {
    setVisible(false);
    setTimeout(onClose, 150);
  };

  const educations = user.educations || [];
  const experiences = user.experiences || [];
  const skills = normalizeNamedList(user.skills);
  const certifications = normalizeNamedList(user.certifications);
  const languages = normalizeNamedList(user.languages);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/60 backdrop-blur-sm px-0 sm:px-4 transition-opacity duration-150 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={requestClose}
    >
      <div
        className={`bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-lg w-full max-h-[92vh] sm:max-h-[85vh] flex flex-col overflow-hidden transition-all duration-150 ${
          visible ? 'translate-y-0 opacity-100 sm:scale-100' : 'translate-y-6 opacity-0 sm:scale-95'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-br from-violet-600 via-fuchsia-500 to-amber-400 px-5 sm:px-6 pt-5 sm:pt-6 pb-5 shrink-0 text-white">
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 rounded-full bg-white/20 text-white hover:bg-white/30 flex items-center justify-center transition"
          >
            <FaTimes size={13} />
          </button>

          <div className="flex items-center gap-3 sm:gap-4 pr-10">
            <Avatar name={user.name} photoSrc={resolvePhotoSrc(user.profile_photo_path)} sizeClass="w-14 h-14 sm:w-16 sm:h-16 text-lg sm:text-xl" />
            <div className="min-w-0">
              <h3 className="font-bold text-base sm:text-lg truncate">{user.name}</h3>
              <p className="text-sm text-white/85 truncate">{user.email}</p>
              <p className="flex items-center gap-1 text-xs text-white/80 mt-1">
                <FaMapMarkerAlt size={10} /> {user.city || 'Location not specified'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            <span className="bg-white rounded-full px-1 py-1">
              <StatusPill status={applicant.status} />
            </span>
            {applicant.match_percentage != null && (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-full bg-white/20">
                ✦ {Math.round(applicant.match_percentage)}% match
              </span>
            )}
          </div>
        </div>

        <div className="overflow-y-auto px-5 sm:px-6 py-4 space-y-3 bg-slate-50/40">
          <MatchBreakdown breakdown={applicant.match_breakdown} />
          <RevealContact applicationId={applicant.id} locked={applicant.contact_locked} phone={user.phone} email={user.email}>
            {(phone) => <ContactActions phone={phone} />}
          </RevealContact>

          <div className="grid grid-cols-2 gap-3">
            <SectionCard icon={<FaBriefcase size={11} />} title="Experience">
              <p className="text-sm text-slate-800 font-medium">{summarizeExperience(user)}</p>
            </SectionCard>
            <SectionCard icon={<FaRupeeSign size={11} />} title="Expected salary">
              <p className="text-sm text-slate-800 font-medium">
                {user.expected_salary ? `₹${Number(user.expected_salary).toLocaleString()}` : 'Not specified'}
              </p>
            </SectionCard>
          </div>

          {user.career_objective && (
            <SectionCard title="Career objective">
              <p className="text-sm text-slate-700 leading-relaxed">{user.career_objective}</p>
            </SectionCard>
          )}

          {skills.length > 0 && (
            <SectionCard title="Skills">
              <div className="flex flex-wrap gap-1.5">
                {skills.map((s) => (
                  <span key={s.key} className="bg-violet-100 text-violet-700 text-xs font-medium px-2.5 py-1 rounded-full">
                    {s.label}
                  </span>
                ))}
              </div>
            </SectionCard>
          )}

          {educations.length > 0 && (
            <SectionCard icon={<FaGraduationCap size={11} />} title="Education">
              <div className="space-y-1.5">
                {educations.map((edu) => (
                  <p key={edu.id} className="text-sm text-slate-700">
                    <span className="font-medium text-slate-900">{edu.degree}</span>
                    {' — '}
                    {edu.institution}
                    {edu.year_of_passing ? <span className="text-slate-400"> · {edu.year_of_passing}</span> : null}
                  </p>
                ))}
              </div>
            </SectionCard>
          )}

          {experiences.length > 0 && (
            <SectionCard icon={<FaBriefcase size={11} />} title="Work history">
              <div className="space-y-1.5">
                {experiences.map((exp) => (
                  <p key={exp.id} className="text-sm text-slate-700">
                    <span className="font-medium text-slate-900">{exp.job_title}</span>
                    {' at '}
                    {exp.company_name}
                  </p>
                ))}
              </div>
            </SectionCard>
          )}

          {certifications.length > 0 && (
            <SectionCard icon={<FaCertificate size={11} />} title="Certifications">
              <div className="space-y-1">
                {certifications.map((cert) => (
                  <p key={cert.key} className="text-sm text-slate-700">{cert.label}</p>
                ))}
              </div>
            </SectionCard>
          )}

          {languages.length > 0 && (
            <SectionCard icon={<FaLanguage size={11} />} title="Languages">
              <p className="text-sm text-slate-700">{languages.map((l) => l.label).join(', ')}</p>
            </SectionCard>
          )}

                    {applicant.status === 'withdrawn' && (
            <SectionCard icon={<FaBan size={10} />} title="Withdrawn by candidate">
              <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                {applicant.withdraw_reason || 'No reason given'}
              </p>
              {applicant.withdrawn_at && (
                <p className="text-xs text-slate-400 mt-1">
                  {new Date(applicant.withdrawn_at).toLocaleString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit',
                  })}
                </p>
              )}
            </SectionCard>
          )}

          {applicant.cover_letter && (
            <SectionCard icon={<FaQuoteLeft size={10} />} title="Cover letter">
              <p className="text-sm text-slate-700 leading-relaxed italic">{applicant.cover_letter}</p>
            </SectionCard>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-4 border-t border-slate-100 bg-white shrink-0">
          {applicant.resume_path ? (
            <button
              type="button"
              onClick={() => window.open(`${BACKEND_URL}/storage/${applicant.resume_path}`, '_blank')}
              className="inline-flex items-center gap-2 text-violet-600 text-sm font-semibold hover:underline bg-transparent border-none p-0 cursor-pointer"
            >
              <FaFileAlt size={13} /> View resume
            </button>
          ) : (
            <span className="text-sm text-slate-400">{applicant.has_resume ? 'Resume available: unlock to view' : 'No resume attached'}</span>
          )}

          <select
            value={applicant.status}
            disabled={applicant.status === 'withdrawn'}
            onChange={(e) => onStatusChange(applicant.id, e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {statusOptions.map((status) => (
              <option key={status} value={status}>{statusLabels[status] || status}</option>
            ))}
            {applicant.status === 'withdrawn' && <option value="withdrawn">Withdrawn</option>}
          </select>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Candidate card                                                      */
/* ------------------------------------------------------------------ */

function CandidateRow({ app, onOpen, onStatusChange, isHighMatch, onViewReason }) {
  const { user } = app;
  const photoSrc = resolvePhotoSrc(user.profile_photo_path);
  const skills = normalizeNamedList(user.skills);
  const educations = user.educations || [];
  const age = calcAge(user.date_of_birth);
  const gLabel = genderLabel(user.gender);
  const applied = daysAgo(app.created_at);
  const active = daysAgo(user.updated_at);
  const monthlySalary = formatMonthlySalary(user.expected_salary);
  const { current, previous } = getCurrentAndPrevious(user.experiences);

  const pct = app.match_percentage;
  const accentColor = pct == null ? 'bg-slate-200' : pct >= 80 ? 'bg-emerald-400' : pct >= 50 ? 'bg-indigo-400' : 'bg-amber-400';

  return (
    <div className="relative bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${accentColor}`} />

      <div className="p-3 sm:p-5 pl-4 sm:pl-6">
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="flex items-start gap-2.5 sm:gap-3 min-w-0">
            <Avatar name={user.name} photoSrc={photoSrc} sizeClass="w-9 h-9 sm:w-11 sm:h-11 text-sm sm:text-base" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <button
                  type="button"
                  onClick={() => onOpen(app.id)}
                  className="font-bold text-slate-900 hover:text-violet-600 transition-colors text-sm sm:text-base"
                >
                  {user.name}
                </button>
                <Link
                  to={`/employer/candidates/${user.id}?application_id=${app.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[11px] sm:text-xs font-semibold text-violet-600 hover:underline whitespace-nowrap"
                >
                  Full profile »
                </Link>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-0.5 mt-1 text-[11px] sm:text-xs text-slate-500">
                {(gLabel || age) && (
                  <span>{[gLabel, age ? `${age} yr` : null].filter(Boolean).join(', ')}</span>
                )}
                <span className="inline-flex items-center gap-1">
                  <FaBriefcase size={9} className="text-slate-400" />
                  {isFresher(user) ? 'Fresher' : summarizeExperience(user)}
                </span>
                {monthlySalary && (
                  <span className="inline-flex items-center gap-1">
                    <FaRupeeSign size={9} className="text-slate-400" /> {monthlySalary}
                  </span>
                )}
                {user.city && (
                  <span className="inline-flex items-center gap-1">
                    <FaMapMarkerAlt size={9} className="text-slate-400" /> {user.city}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end gap-1.5 sm:gap-2 shrink-0">
            <MatchRing percent={app.match_percentage} size={38} />
            <StatusPill status={app.status} />
            {app.status === 'withdrawn' && app.withdraw_reason && (
              <button
                type="button"
                onClick={() => onViewReason(app)}
                className="text-[11px] font-semibold text-violet-600 hover:underline"
              >
                View reason
              </button>
            )}
          </div>
        </div>

        {isHighMatch && <MatchBreakdown breakdown={app.match_breakdown} />}

        {(current || previous || educations.length > 0 || user.languages?.length > 0) && (
          <div className="mt-3 sm:ml-[56px] grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs sm:text-sm">
            {current && (
              <>
                <span className="text-slate-400 shrink-0">Current / Latest</span>
                <span className="font-medium text-slate-900 bg-amber-50 px-1.5 py-0.5 rounded w-fit">
                  {current.job_title}{current.job_title && current.company_name ? ' at ' : ''}{current.company_name}
                </span>
              </>
            )}
            {previous && (
              <>
                <span className="text-slate-400 shrink-0">Previous</span>
                <span className="text-slate-700">
                  {previous.job_title}{previous.job_title && previous.company_name ? ' at ' : ''}{previous.company_name}
                </span>
              </>
            )}
            {educations.length > 0 && (
              <>
                <span className="text-slate-400 shrink-0">Education</span>
                <span className="text-slate-700">
                  {educations.map((e, i) => {
                    const years = [e.start_year, e.end_year].filter(Boolean).join(' - ');
                    return (
                      <span key={i}>
                        {i > 0 && ' | '}
                        {e.degree}{e.institution ? `, ${e.institution}` : ''}{years ? ` · ${years}` : ''}
                      </span>
                    );
                  })}
                </span>
              </>
            )}
            {user.languages?.length > 0 && (
              <>
                <span className="text-slate-400 shrink-0">Language</span>
                <span className="text-slate-700">
                  {user.languages.map((l, i) => {
                    const label = extractLabel(l);
                    return label ? (
                      <span key={i}>
                        {i > 0 && ' | '}
                        {label}{l.proficiency ? ` (${l.proficiency})` : ''}
                      </span>
                    ) : null;
                  })}
                </span>
              </>
            )}
          </div>
        )}

        {skills.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3 sm:ml-[56px]">
            {skills.slice(0, 5).map((s) => (
              <span key={s.key} className="bg-violet-50 text-violet-700 text-[11px] sm:text-xs font-medium px-2.5 py-1 rounded-full">
                {s.label}
              </span>
            ))}
            {skills.length > 5 && <span className="text-[11px] sm:text-xs text-slate-400 px-1 py-1">+{skills.length - 5} more</span>}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-4 sm:ml-[56px]">
          <RevealContact applicationId={app.id} locked={app.contact_locked} phone={user.phone} email={user.email}>
            {(phone) => <>
              <PhoneChip phone={phone} />
              <ContactActions phone={phone} />
            </>}
          </RevealContact>
          {app.resume_path && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`${BACKEND_URL}/storage/${app.resume_path}`, '_blank');
              }}
              className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-semibold px-2.5 sm:px-3 py-1.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 transition"
            >
              <FaDownload size={10} /> Resume
            </button>
          )}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mt-4 pt-3 border-t border-slate-100 sm:ml-[56px]">
          <div className="flex items-center gap-2 text-[11px] sm:text-xs text-slate-400">
            {applied && <span>Applied {applied}</span>}
            {applied && active && <span>·</span>}
            {active && <span>Active {active}</span>}
          </div>

         <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-2">
  <button
    type="button"
    onClick={() => onStatusChange(app.id, 'rejected')}
    disabled={app.status === 'rejected' || app.status === 'withdrawn'}
    className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 sm:py-1.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
  >
    <FaBan size={10} /> Reject
  </button>
  <button
    type="button"
    onClick={() => onStatusChange(app.id, 'shortlisted')}
    disabled={app.status === 'shortlisted' || app.status === 'withdrawn'}
    className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3.5 py-2 sm:py-1.5 rounded-lg text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
  >
    <FaStar size={10} /> Shortlist
  </button>
  <select
    value={app.status}
    disabled={app.status === 'withdrawn'}
    onChange={(e) => onStatusChange(app.id, e.target.value)}
    className="col-span-2 sm:col-span-1 w-full sm:w-auto text-xs border border-slate-200 rounded-lg px-2.5 py-2 sm:py-1.5 bg-slate-50 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {statusOptions.map((status) => (
      <option key={status} value={status}>{statusLabels[status] || status}</option>
    ))}
    {app.status === 'withdrawn' && <option value="withdrawn">Withdrawn</option>}
  </select>
</div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

const HIGH_MATCH_THRESHOLD = 80;

const extractApplicantsArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.applicants)) return payload.applicants;
  if (Array.isArray(payload?.applications)) return payload.applications;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data; // nested paginator
  console.warn('Unexpected applicants payload shape:', payload);
  return [];
};

function EmployerApplicants() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);

  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [selectedApplicantId, setSelectedApplicantId] = useState(null);
    const [reasonApplicant, setReasonApplicant] = useState(null);
  const selectedApplicant = Array.isArray(applicants)
    ? applicants.find((a) => a.id === selectedApplicantId) || null
    : null;

  const [statusFilter, setStatusFilter] = useState('all');
  const [onlyResumeAttached, setOnlyResumeAttached] = useState(false);
  const [onlyHighMatch, setOnlyHighMatch] = useState(false);
  const [sortBy, setSortBy] = useState('relevance'); // 'relevance' | 'newest'

  const [appliedInDays, setAppliedInDays] = useState(null); // null | 3 | 7 | 15 | 30
  const [locationQuery, setLocationQuery] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [expMin, setExpMin] = useState('');
  const [expMax, setExpMax] = useState('');
  const [monthlySalaryMax, setMonthlySalaryMax] = useState('');
  const [educationQuery, setEducationQuery] = useState('');
  const [languageQuery, setLanguageQuery] = useState('');

  const [openSections, setOpenSections] = useState({ appliedIn: true });
  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const [filtersOpen, setFiltersOpen] = useState(false); // mobile only — desktop always shows
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // 👈 add

  const fetchApplicants = async () => {
    try {
      const response = await api.get(`/jobs/${jobId}/applicants`);
      setApplicants(extractApplicantsArray(response.data));
    } catch (err) {
      console.error('Failed to fetch applicants', err);
      setApplicants([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchApplicants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    const refresh = () => fetchApplicants();
    window.addEventListener('applicant-contact-unlocked', refresh);
    return () => window.removeEventListener('applicant-contact-unlocked', refresh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      // Routed through Pipeline bulk-move (not /applications/{id}/status) —
      // that endpoint's status enum (6-stage) no longer matches this screen's
      // 9-stage dropdown. bulk-move accepts a single-id array and shares the
      // same enum as `statusOptions` above.
      await api.post('/pipeline/bulk-move', { ids: [applicationId], status: newStatus });
      setApplicants((prev) =>
        (Array.isArray(prev) ? prev : []).map((a) =>
          a.id === applicationId ? { ...a, status: newStatus } : a
        )
      );
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const counts = useMemo(() => {
    const list = Array.isArray(applicants) ? applicants : [];
    const base = { all: list.length };
    statusOptions.forEach((s) => {
      base[s] = list.filter((a) => a.status === s).length;
    });

        base.withdrawn = list.filter((a) => a.status === 'withdrawn').length;
    return base;
  }, [applicants]);

  const inInterviewCount = (counts.interview_round_1 || 0) + (counts.interview_round_2 || 0) + (counts.hr_round || 0);
 const selectedCount = (counts.offer || 0) + (counts.joined || 0);

  const filtered = useMemo(() => {
    let list = Array.isArray(applicants) ? applicants : [];
    if (statusFilter !== 'all') {
  const allowed = STATUS_GROUPS[statusFilter] || [statusFilter];
  list = list.filter((a) => allowed.includes(a.status));
}
    if (onlyResumeAttached) list = list.filter((a) => !!(a.resume_path || a.has_resume));
    if (onlyHighMatch) list = list.filter((a) => (a.match_percentage ?? 0) >= HIGH_MATCH_THRESHOLD);

    if (appliedInDays) {
      const cutoff = Date.now() - appliedInDays * 86400000;
      list = list.filter((a) => a.created_at && new Date(a.created_at).getTime() >= cutoff);
    }
    if (locationQuery.trim()) {
      const q = locationQuery.trim().toLowerCase();
      list = list.filter((a) => (a.user.city || '').toLowerCase().includes(q));
    }
    if (genderFilter) {
      list = list.filter((a) => a.user.gender === genderFilter);
    }
    if (ageMin !== '' || ageMax !== '') {
      list = list.filter((a) => {
        const age = calcAge(a.user.date_of_birth);
        if (age == null) return false;
        if (ageMin !== '' && age < Number(ageMin)) return false;
        if (ageMax !== '' && age > Number(ageMax)) return false;
        return true;
      });
    }
    if (expMin !== '' || expMax !== '') {
      list = list.filter((a) => {
        const yrs = totalExperienceYears(a.user);
        if (expMin !== '' && yrs < Number(expMin)) return false;
        if (expMax !== '' && yrs > Number(expMax)) return false;
        return true;
      });
    }
    if (monthlySalaryMax !== '') {
      list = list.filter((a) => {
        const annual = a.user.expected_salary;
        if (annual == null || annual === '') return true; // unspecified salary isn't excluded
        return Number(annual) / 12 <= Number(monthlySalaryMax);
      });
    }
    if (educationQuery.trim()) {
      const q = educationQuery.trim().toLowerCase();
      list = list.filter((a) =>
        (a.user.educations || []).some((e) => (e.degree || '').toLowerCase().includes(q))
      );
    }
    if (languageQuery.trim()) {
      const q = languageQuery.trim().toLowerCase();
      list = list.filter((a) =>
        normalizeNamedList(a.user.languages).some((l) => l.label.toLowerCase().includes(q))
      );
    }

    const sorted = [...list].sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      return (b.match_percentage ?? -1) - (a.match_percentage ?? -1);
    });
    return sorted;
  }, [
    applicants, statusFilter, onlyResumeAttached, onlyHighMatch, sortBy,
    appliedInDays, locationQuery, genderFilter, ageMin, ageMax,
    expMin, expMax, monthlySalaryMax, educationQuery, languageQuery,
  ]);

  const highMatches = filtered.filter((a) => (a.match_percentage ?? 0) >= HIGH_MATCH_THRESHOLD);
  const otherMatches = filtered.filter((a) => (a.match_percentage ?? 0) < HIGH_MATCH_THRESHOLD);

  const applicantsList = Array.isArray(applicants) ? applicants : [];
  const resumeAttachedCount = applicantsList.filter((a) => !!(a.resume_path || a.has_resume)).length;
  const highMatchCount = applicantsList.filter((a) => (a.match_percentage ?? 0) >= HIGH_MATCH_THRESHOLD).length;

  const activeFilterCount = [
    onlyResumeAttached,
    onlyHighMatch,
    appliedInDays,
    locationQuery.trim(),
    genderFilter,
    ageMin !== '',
    ageMax !== '',
    expMin !== '',
    expMax !== '',
    monthlySalaryMax !== '',
    educationQuery.trim(),
    languageQuery.trim(),
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setOnlyResumeAttached(false);
    setOnlyHighMatch(false);
    setAppliedInDays(null);
    setLocationQuery('');
    setGenderFilter('');
    setAgeMin('');
    setAgeMax('');
    setExpMin('');
    setExpMax('');
    setMonthlySalaryMax('');
    setEducationQuery('');
    setLanguageQuery('');
  };

  return (
  <div className="min-h-screen bg-yellow-50">

    <Navbar user={user} />
    <EmployerSidebar user={user} activeTab="pipeline" />

    <div className="lg:pl-16 lg:peer-hover:pl-64 transition-all duration-200 ease-in-out">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-10">

        <div className="lg:hidden mb-3">
          <button
            type="button"
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="w-full flex items-center justify-between bg-white rounded-xl border border-slate-100 shadow-sm px-4 py-3 text-sm font-semibold text-slate-700"
          >
            <span>Menu</span>
            <span className={`text-slate-400 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>
          {mobileMenuOpen && (
            <div className="mt-2">
              <EmployerSidebar user={user} activeTab="pipeline" mobile />
            </div>
          )}
        </div>

        {loading ? (
            <p className="text-slate-500">Loading...</p>
          ) : applicantsList.length === 0 ? (
            <p className="text-slate-500">No applicants yet for this job.</p>
          ) : (
            <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 items-start">
              {/* Filters sidebar */}
              <div className="w-full lg:w-72 shrink-0 space-y-4 lg:sticky lg:top-8 lg:max-h-[85vh] lg:overflow-y-auto pr-1">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">

                  {/* Header — tappable to expand/collapse on mobile */}
                  <button
                    type="button"
                    onClick={() => setFiltersOpen((v) => !v)}
                    className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-500 px-4 py-3.5 flex items-center justify-between lg:cursor-default"
                  >
                    <span className="flex items-center gap-2 text-white font-bold text-sm">
                      <FaSlidersH size={13} /> Filters
                      {activeFilterCount > 0 && (
                        <span className="bg-white/25 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                          {activeFilterCount}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-3">
                      {activeFilterCount > 0 && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); clearAllFilters(); }}
                          className="flex items-center gap-1 text-[11px] font-semibold text-white/90 hover:text-white"
                        >
                          <FaRedoAlt size={9} /> Clear all
                        </span>
                      )}
                      <span className={`text-white transition-transform lg:hidden ${filtersOpen ? 'rotate-180' : ''}`}>▾</span>
                    </span>
                  </button>

                  <div className={`p-4 ${filtersOpen ? 'block' : 'hidden'} lg:block`}>
                    {/* Quick toggles */}
                    <div className="space-y-2 pb-1">
                      <button
                        type="button"
                        onClick={() => setOnlyHighMatch((v) => !v)}
                        className={`w-full flex items-center justify-between text-left text-sm font-medium px-3 py-2.5 rounded-xl border transition ${
                          onlyHighMatch
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <FaStar size={12} className={onlyHighMatch ? 'text-emerald-500' : 'text-slate-400'} />
                          Matched candidates
                        </span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          onlyHighMatch ? 'bg-emerald-200/60 text-emerald-800' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {highMatchCount}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setOnlyResumeAttached((v) => !v)}
                        className={`w-full flex items-center justify-between text-left text-sm font-medium px-3 py-2.5 rounded-xl border transition ${
                          onlyResumeAttached
                            ? 'border-sky-300 bg-sky-50 text-sky-700'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <FaFileAlt size={12} className={onlyResumeAttached ? 'text-sky-500' : 'text-slate-400'} />
                          Resume attached
                        </span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          onlyResumeAttached ? 'bg-sky-200/60 text-sky-800' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {resumeAttachedCount}
                        </span>
                      </button>
                    </div>

                    <FilterSection
                      title="Applied in"
                      icon={<FaCalendarAlt size={12} />}
                      open={!!openSections.appliedIn}
                      onToggle={() => toggleSection('appliedIn')}
                    >
                      <div className="grid grid-cols-2 gap-2">
                        {[3, 7, 15, 30].map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setAppliedInDays(appliedInDays === d ? null : d)}
                            className={`text-xs font-semibold px-2.5 py-1.5 rounded-full border transition ${
                              appliedInDays === d
                                ? 'border-violet-400 text-white bg-violet-500'
                                : 'border-slate-200 text-slate-600 hover:border-violet-200 hover:text-violet-600'
                            }`}
                          >
                            {d}d
                          </button>
                        ))}
                      </div>
                    </FilterSection>

                    <FilterSection
                      title="Location"
                      icon={<FaMapMarkerAlt size={12} />}
                      open={!!openSections.location}
                      onToggle={() => toggleSection('location')}
                    >
                      <input
                        type="text"
                        value={locationQuery}
                        onChange={(e) => setLocationQuery(e.target.value)}
                        placeholder="City"
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
                      />
                    </FilterSection>

                    <FilterSection
                      title="Gender"
                      icon={<FaVenusMars size={12} />}
                      open={!!openSections.gender}
                      onToggle={() => toggleSection('gender')}
                    >
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          { value: '', label: 'Any' },
                          { value: 'male', label: 'Male' },
                          { value: 'female', label: 'Female' },
                          { value: 'non-binary', label: 'Non-binary' },
                          { value: 'other', label: 'Other' },
                        ].map((opt) => (
                          <button
                            key={opt.value || 'any'}
                            type="button"
                            onClick={() => setGenderFilter(opt.value)}
                            className={`text-xs font-medium px-2.5 py-1.5 rounded-full border transition ${
                              genderFilter === opt.value
                                ? 'border-violet-400 text-white bg-violet-500'
                                : 'border-slate-200 text-slate-600 hover:border-violet-200'
                            }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </FilterSection>

                    <FilterSection
                      title="Age"
                      icon={<FaUsers size={12} />}
                      open={!!openSections.age}
                      onToggle={() => toggleSection('age')}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          value={ageMin}
                          onChange={(e) => setAgeMin(e.target.value)}
                          placeholder="Min"
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        />
                        <span className="text-slate-300 text-xs">–</span>
                        <input
                          type="number"
                          min="0"
                          value={ageMax}
                          onChange={(e) => setAgeMax(e.target.value)}
                          placeholder="Max"
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        />
                      </div>
                    </FilterSection>

                    <FilterSection
                      title="Experience"
                      icon={<FaBriefcase size={12} />}
                      open={!!openSections.exp}
                      onToggle={() => toggleSection('exp')}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          value={expMin}
                          onChange={(e) => setExpMin(e.target.value)}
                          placeholder="Min yrs"
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        />
                        <span className="text-slate-300 text-xs">–</span>
                        <input
                          type="number"
                          min="0"
                          value={expMax}
                          onChange={(e) => setExpMax(e.target.value)}
                          placeholder="Max yrs"
                          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                        />
                      </div>
                    </FilterSection>

                    <FilterSection
                      title="Monthly salary"
                      icon={<FaRupeeSign size={12} />}
                      open={!!openSections.salary}
                      onToggle={() => toggleSection('salary')}
                    >
                      <input
                        type="number"
                        min="0"
                        value={monthlySalaryMax}
                        onChange={(e) => setMonthlySalaryMax(e.target.value)}
                        placeholder="Max ₹/month"
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      />
                    </FilterSection>

                    <FilterSection
                      title="Education"
                      icon={<FaGraduationCap size={12} />}
                      open={!!openSections.education}
                      onToggle={() => toggleSection('education')}
                    >
                      <input
                        type="text"
                        value={educationQuery}
                        onChange={(e) => setEducationQuery(e.target.value)}
                        placeholder="e.g. B.Tech, 12th"
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      />
                    </FilterSection>

                    <FilterSection
                      title="Languages"
                      icon={<FaLanguage size={12} />}
                      open={!!openSections.languages}
                      onToggle={() => toggleSection('languages')}
                    >
                      <input
                        type="text"
                        value={languageQuery}
                        onChange={(e) => setLanguageQuery(e.target.value)}
                        placeholder="e.g. Kannada"
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                      />
                    </FilterSection>
                  </div>
                </div>
              </div>

              {/* Right column: heading + stats + candidate list */}
              <div className="flex-1 w-full space-y-4 min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-violet-600 via-fuchsia-500 to-amber-500 bg-clip-text text-transparent">
                  Applicants
                </h2>

                <div className="grid grid-cols-3 gap-2">
                  <StatPill
                    icon={<FaUsers size={14} />} iconBg="bg-violet-600" iconColor="text-white"
                    count={counts.all} label="All candidates"
                    active={statusFilter === 'all'} onClick={() => setStatusFilter('all')}
                  />
                  <StatPill
                    icon={<FaHourglassHalf size={13} />} iconBg="bg-amber-500" iconColor="text-white"
                    count={counts.applied} label="Action Pending"
                    active={statusFilter === 'applied'} onClick={() => setStatusFilter('applied')}
                  />
                  <StatPill
                    icon={<FaStar size={13} />} iconBg="bg-fuchsia-600" iconColor="text-white"
                    count={counts.shortlisted} label="Shortlisted"
                    active={statusFilter === 'shortlisted'} onClick={() => setStatusFilter('shortlisted')}
                  />
                <StatPill
  icon={<FaCommentDots size={13} />} iconBg="bg-purple-600" iconColor="text-white"
  count={inInterviewCount} label="In Interview"
  active={statusFilter === 'interview'}
  onClick={() => setStatusFilter('interview')}
/>
<StatPill
  icon={<FaCheckCircle size={13} />} iconBg="bg-emerald-600" iconColor="text-white"
  count={selectedCount} label="Selected"
  active={statusFilter === 'selected'}
  onClick={() => setStatusFilter('selected')}
/>
                  <StatPill
                    icon={<FaBan size={13} />} iconBg="bg-rose-600" iconColor="text-white"
                    count={counts.rejected} label="Rejected"
                    active={statusFilter === 'rejected'} onClick={() => setStatusFilter('rejected')}
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-slate-500">
                  <span>Showing {filtered.length} candidate{filtered.length === 1 ? '' : 's'}</span>
                  <label className="flex items-center gap-2">
                    Sort by:
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="text-sm border border-slate-200 rounded-lg px-2 py-1 bg-white"
                    >
                      <option value="relevance">Relevance</option>
                      <option value="newest">Newest</option>
                    </select>
                  </label>
                </div>

                {filtered.length === 0 ? (
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-10 text-center text-slate-500">
                    No candidates match these filters.
                  </div>
                ) : (
                  <>
                    {highMatches.length > 0 && (
                      <div className="space-y-3">
                        <div className="bg-gradient-to-r from-violet-50 to-amber-50 rounded-2xl p-3 border border-violet-100">
                          <p className="text-sm font-bold bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent">✦ High Matches</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Candidates matching all your key requirements. Connect with them before someone else does!
                          </p>
                        </div>
                        {highMatches.map((app) => (
                          <CandidateRow
                            key={app.id}
                            app={app}
                            isHighMatch
                            onOpen={setSelectedApplicantId}
                            onStatusChange={handleStatusChange}
                            onViewReason={setReasonApplicant}
                          />
                        ))}
                      </div>
                    )}

                    {otherMatches.length > 0 && (
                      <div className="space-y-3">
                        {highMatches.length > 0 && (
                          <p className="text-sm font-semibold text-slate-500 pt-2">Other candidates</p>
                        )}
                        {otherMatches.map((app) => (
                          <CandidateRow
                            key={app.id}
                            app={app}
                            isHighMatch={false}
                            onOpen={setSelectedApplicantId}
                            onStatusChange={handleStatusChange}
                            onViewReason={setReasonApplicant}
                          />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

                 {reasonApplicant && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4"
          onClick={() => setReasonApplicant(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-bold bg-gradient-to-r from-violet-600 to-fuchsia-500 bg-clip-text text-transparent">
                Withdraw Reason
              </h3>
              <button
                type="button"
                onClick={() => setReasonApplicant(null)}
                className="text-slate-400 hover:text-violet-600"
                aria-label="Close"
              >
                <FaTimes size={14} />
              </button>
            </div>
            <div className="space-y-2 text-sm text-slate-700">
              <p><span className="text-slate-400">Candidate:</span> {reasonApplicant.user?.name || '—'}</p>
              <p className="whitespace-pre-wrap break-words">
                <span className="text-slate-400">Reason:</span> {reasonApplicant.withdraw_reason}
              </p>
              <p>
                <span className="text-slate-400">Withdrawn on:</span>{' '}
                {reasonApplicant.withdrawn_at
                  ? new Date(reasonApplicant.withdrawn_at).toLocaleString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      )}

      {selectedApplicant && (
        <ApplicantModal
          applicant={selectedApplicant}
          onClose={() => setSelectedApplicantId(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}

export default EmployerApplicants;